import CheckCircle from '@mui/icons-material/CheckCircle'
import PendingActions from '@mui/icons-material/PendingActions'
import AccountCircle from '@mui/icons-material/AccountCircle'
import ExitToApp from '@mui/icons-material/ExitToApp'
import Person from '@mui/icons-material/Person'
import { Avatar, IconButton, Menu, MenuItem, Box, Typography, Divider, Tooltip, Chip } from '@mui/material'
import Link from '@mui/material/Link'
import { useContext, useEffect, useState, useMemo } from 'react'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getImageLink, logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  HeaderContainer,
  HeaderGreeting,
  HeaderLinkText,
  HeaderStatusOfUpdates,
} from './MentorHeaderStyles'
import PortalSwitcherButton from './PortalSwitcherButton'
import CommsQuotaDialog from 'src/clinic/components/CommsQuotaDialog'
import { getClinicBrandCommsQuota } from 'src/clinic/services/commsQuotaService'

export default function MentorHeader({
  greetingsEnabled,
  headerLinks,
  liveChecklist,
  heading,
  agreements,
  subHeading,
  translatedStrings,
}) {
  const { mentorProfileData, setMentorProfileData, mentorClinicAssociationData } = useContext(MentorProfileContext)
  const navigate = useNavigate()
  const location = useLocation()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false)
  const [quotaSummary, setQuotaSummary] = useState({ waCredits: null, outcallCredits: null })
  const [quotaLoading, setQuotaLoading] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Get clinic brands where user is owner/admin
  const ownedClinicBrands = useMemo(() => {
    if (!mentorClinicAssociationData || !mentorProfileData?.userId) return []
    
    const uniqueBrands = new Map()
    const isAdmin = getAllRolesInBoolean().isClinicAdmin
    
    mentorClinicAssociationData.forEach((item) => {
      if (item?.clinicBrandId) {
        // Check if user is owner
        const isOwner = item?.clinicBrandOwnerUserIds?.includes(mentorProfileData.userId)
        // Check if user is admin (admins might be in owner list or have separate admin field)
        const isAdminForBrand = isAdmin && (
          item?.clinicBrandOwnerUserIds?.includes(mentorProfileData.userId) ||
          item?.clinicBrandAdminUserIds?.includes(mentorProfileData.userId) ||
          item?.adminUserIds?.includes(mentorProfileData.userId)
        )
        
        if (isOwner || isAdminForBrand) {
          if (!uniqueBrands.has(item.clinicBrandId)) {
            uniqueBrands.set(item.clinicBrandId, {
              id: item.clinicBrandId,
              brandName: item.clinicBrandName,
              title: item.clinicBrandName,
              clinicBrandName: item.clinicBrandName,
            })
          }
        }
      }
    })
    return Array.from(uniqueBrands.values())
  }, [mentorClinicAssociationData, mentorProfileData?.userId])

  const isClinicOwnerOrAdmin = getAllRolesInBoolean().isClinicOwner || getAllRolesInBoolean().isClinicAdmin

  // Fetch quota summary for first brand
  useEffect(() => {
    if (isClinicOwnerOrAdmin && ownedClinicBrands.length > 0 && open) {
      const firstBrand = ownedClinicBrands[0]
      fetchQuotaSummary(firstBrand.id)
    } else {
      setQuotaSummary({ waCredits: null, outcallCredits: null })
    }
  }, [isClinicOwnerOrAdmin, ownedClinicBrands, open])

  const fetchQuotaSummary = async (brandId) => {
    if (!brandId) return
    setQuotaLoading(true)
    try {
      const data = await getClinicBrandCommsQuota(brandId)
      // Ensure we're extracting numeric values correctly
      const waCredits = data?.waCredits !== undefined ? Number(data.waCredits) : null
      const outcallCredits = data?.outcallCredits !== undefined ? Number(data.outcallCredits) : null
      
      setQuotaSummary({
        waCredits,
        outcallCredits,
      })
    } catch (error) {
      console.error('Error fetching quota summary:', error)
      setQuotaSummary({ waCredits: null, outcallCredits: null })
    } finally {
      setQuotaLoading(false)
    }
  }
  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }
  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth)

    // Clean up the event listener on component unmount
    return () => window.removeEventListener('resize', updateWindowWidth)
  }, []) // Empty dependency array ensures the event listener is only added once

  const showPortalSwitcher =
    location?.pathname?.startsWith('/mentor-home/my-clinic-associations')

  return (
    <>
      <HeaderContainer className="hide-on-print-only">
        {greetingsEnabled && (
          <GenericDisplayFlexColumn sx={{ gap: 0.25 }}>
            <HeaderGreeting>{translatedStrings?.greetings || 'Welcome'}</HeaderGreeting>
            <HeaderGreeting main>{mentorProfileData?.name || 'Doctor'}</HeaderGreeting>
            {getAllRolesInBoolean().isClinicOwner && getAllRolesInBoolean().isDoctor && (
              <Box
                component="div"
                sx={{
                  fontSize: '12px',
                  color: 'rgba(100, 116, 139, 0.8)',
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <span>Using as doctor.</span>
                <Box
                  component="span"
                  onClick={() => navigate('/clinic-home')}
                  sx={{
                    cursor: 'pointer',
                    color: 'rgba(16, 185, 129, 0.9)',
                    fontWeight: 500,
                    textDecoration: 'none',
                    borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'rgba(16, 185, 129, 1)',
                      borderBottomColor: 'rgba(16, 185, 129, 0.6)',
                    },
                  }}
                >
                  Switch to clinic owner?
                </Box>
              </Box>
            )}
          </GenericDisplayFlexColumn>
        )}
        {(heading || subHeading) && (
          <GenericDisplayFlexColumn>
            {subHeading && <HeaderGreeting>{subHeading}</HeaderGreeting>}
            {heading && <HeaderGreeting main>{heading}</HeaderGreeting>}
          </GenericDisplayFlexColumn>
        )}

        <GenericDisplayFlex sx={{ alignItems: 'center', gap: 1 }}>
          {headerLinks?.map((item, index) => {
            return (
              <HeaderLinkText key={index} onClick={() => navigate(item?.link)}>
                {item?.displayName}
              </HeaderLinkText>
            )
          })}

          {showPortalSwitcher && <PortalSwitcherButton />}

          <Tooltip title="Account Menu" arrow>
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{
                padding: '6px',
                border: '2px solid rgba(16, 185, 129, 0.2)',
                backgroundColor: 'rgba(16, 185, 129, 0.04)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.12)',
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                src={getImageLink(mentorProfileData?.profilePicId)}
                sx={{
                  width: 40,
                  height: 40,
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                }}
              >
                {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 1.5,
                minWidth: 240,
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  borderLeft: '1px solid rgba(16, 185, 129, 0.1)',
                  borderTop: '1px solid rgba(16, 185, 129, 0.1)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User Info Section */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
                backgroundColor: 'rgba(16, 185, 129, 0.02)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'rgba(15, 23, 42, 0.95)',
                  fontSize: '14px',
                  mb: 0.5,
                }}
              >
                {mentorProfileData?.name || 'Doctor'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(100, 116, 139, 0.8)',
                  fontSize: '12px',
                }}
              >
                Mentor Portal
              </Typography>
            </Box>

            {/* Mobile Navigation Links */}
            {windowWidth < 800 &&
              headerLinks?.map((headerLink, idx) => {
                return (
                  <MenuItem
                    key={idx}
                    onClick={() => {
                      navigate(headerLink?.link)
                      handleClose()
                    }}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: '14px',
                      color: 'rgba(71, 85, 105, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        color: 'rgba(16, 185, 129, 0.95)',
                      },
                    }}
                  >
                    {headerLink?.displayName}
                  </MenuItem>
                )
              })}

            {windowWidth < 800 && headerLinks?.length > 0 && <Divider sx={{ my: 0.5 }} />}

            {/* Communication Quota Summary - Only for Clinic Owners/Admins */}
            {isClinicOwnerOrAdmin && ownedClinicBrands.length > 0 && (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.04)',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setQuotaDialogOpen(true)
                    handleClose()
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(100, 116, 139, 0.8)',
                      fontSize: '11px',
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    Communication Quota
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {quotaLoading ? (
                      <Typography variant="caption" color="text.secondary">
                        Loading...
                      </Typography>
                    ) : (
                      <>
                        {quotaSummary.waCredits !== null && (
                          <Chip
                            label={`WA: ${quotaSummary.waCredits}`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(16, 185, 129, 0.1)',
                              color: 'rgba(16, 185, 129, 0.9)',
                              fontSize: '11px',
                              height: '24px',
                            }}
                          />
                        )}
                        {quotaSummary.outcallCredits !== null && (
                          <Chip
                            label={`Outcall: ${quotaSummary.outcallCredits}`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              color: 'rgba(239, 68, 68, 0.9)',
                              fontSize: '11px',
                              height: '24px',
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            {/* Profile Menu Item */}
            <MenuItem
              onClick={() => {
                navigate('/mentor-home/profile')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(71, 85, 105, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  color: 'rgba(16, 185, 129, 0.95)',
                },
              }}
            >
              <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(16, 185, 129, 0.7)' }} />
              Profile Settings
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* Logout */}
            <MenuItem
              onClick={() => {
                logoutHandlerMentor()
                navigate('/')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(239, 68, 68, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  color: 'rgba(239, 68, 68, 1)',
                },
              }}
            >
              <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </GenericDisplayFlex>
      </HeaderContainer>

      {liveChecklist && (
        <HeaderStatusOfUpdates>
          <GenericDisplayFlex>Your Checklist: </GenericDisplayFlex>

          {Array.isArray(mentorProfileData?.schedules) &&
          mentorProfileData?.schedules?.length > 0 ? (
            <GenericDisplayFlex style={{ gap: '4px' }}>
              1. You have Active Schedules <CheckCircle sx={{ color: 'green', fontSize: '14px' }} />
            </GenericDisplayFlex>
          ) : (
            <GenericDisplayFlex style={{ gap: '4px' }}>
              1. Set your Availability <PendingActions sx={{ color: 'blue', fontSize: '14px' }} />
            </GenericDisplayFlex>
          )}

          {Array.isArray(mentorProfileData?.eventTypes) &&
          mentorProfileData?.eventTypes?.length > 0 &&
          mentorProfileData?.eventTypes?.some((item) => item?.enabled) ? (
            <GenericDisplayFlex style={{ gap: '4px' }}>
              2. You have Active Events <CheckCircle sx={{ color: 'green', fontSize: '14px' }} />
            </GenericDisplayFlex>
          ) : (
            <GenericDisplayFlex style={{ gap: '4px' }}>
              2. Create your First Event <PendingActions sx={{ color: 'blue', fontSize: '14px' }} />
            </GenericDisplayFlex>
          )}

          {Array.isArray(mentorProfileData?.schedules) &&
          mentorProfileData?.schedules?.length > 0 &&
          mentorProfileData?.schedules?.some((item) => item?.defaultSchedule) ? (
            (
              Array.isArray(
                mentorProfileData?.schedules?.find((item) => item?.defaultSchedule)?.eventIds,
              ) && mentorProfileData?.schedules?.find((item) => item?.defaultSchedule)
            )?.eventIds?.length > 0 ? (
              <GenericDisplayFlex style={{ gap: '4px' }}>
                3. You have events in your default Schedule{' '}
                <CheckCircle sx={{ color: 'green', fontSize: '14px' }} />
              </GenericDisplayFlex>
            ) : (
              <GenericDisplayFlex style={{ gap: '4px' }}>
                3. Put Events in your default Schedule{' '}
                <PendingActions sx={{ color: 'blue', fontSize: '14px' }} />
              </GenericDisplayFlex>
            )
          ) : (
            <GenericDisplayFlex style={{ gap: '4px' }}>
              3. Select a default schedule{' '}
              <PendingActions sx={{ color: 'blue', fontSize: '14px' }} />
            </GenericDisplayFlex>
          )}
        </HeaderStatusOfUpdates>
      )}
      {!!agreements && agreements > 0 && (
        <HeaderStatusOfUpdates customColor={'#bcbcbc'}>
          <GenericDisplayFlex gap={5}>
            You have {agreements} agreements to sign.📋 Go to
            <Link to={'/mentor-home/agreements'} component={RouterLink} underline={'hover'}>
              Agreements, Tax and Banking.
            </Link>
          </GenericDisplayFlex>
        </HeaderStatusOfUpdates>
      )}

      {/* Communication Quota Dialog */}
      {isClinicOwnerOrAdmin && (
        <CommsQuotaDialog
          open={quotaDialogOpen}
          onClose={() => setQuotaDialogOpen(false)}
          clinicBrands={ownedClinicBrands}
        />
      )}
    </>
  )
}
